import React from 'react'
import { TrendingUp, TrendingDown, Minus } from 'lucide-react'
import { cn } from '@/utils/cn'
import { formatNumber } from '@/utils/formatters'

interface MetricsCardProps {
  title: string
  value: number | string
  icon: React.ReactNode
  trend?: number
  trendLabel?: string
  suffix?: string
  color?: 'brand' | 'success' | 'warning' | 'error' | 'info'
}

const colorClasses = {
  brand: {
    icon: 'text-[var(--color-brand)] bg-[var(--color-brand-light)]',
  },
  success: {
    icon: 'text-[var(--color-success)] bg-[rgba(16,185,129,0.1)]',
  },
  warning: {
    icon: 'text-[var(--color-warning)] bg-[rgba(245,158,11,0.1)]',
  },
  error: {
    icon: 'text-[var(--color-error)] bg-[rgba(239,68,68,0.1)]',
  },
  info: {
    icon: 'text-[var(--color-info)] bg-[rgba(59,130,246,0.1)]',
  },
}

export function MetricsCard({
  title,
  value,
  icon,
  trend,
  trendLabel,
  suffix,
  color = 'brand',
}: MetricsCardProps) {
  const isPositive = trend !== undefined && trend > 0
  const isNegative = trend !== undefined && trend < 0
  const isNeutral = trend !== undefined && trend === 0

  const formattedValue =
    typeof value === 'number' ? formatNumber(value) : value

  return (
    <div className="glass-card p-6 h-full min-h-[140px] flex flex-col justify-between">
      <div className="flex items-start justify-between">
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-[var(--text-muted)] mb-2">
            {title}
          </p>
          <div className="flex items-baseline gap-1">
            <span className="text-3xl font-bold text-[var(--text-primary)]">
              {formattedValue}
            </span>
            {suffix && (
              <span className="text-base font-medium text-[var(--text-secondary)]">
                {suffix}
              </span>
            )}
          </div>

          {trend !== undefined && (
            <div
              className={cn(
                'flex items-center gap-1 mt-2 text-xs font-medium',
                isPositive && 'trend-up',
                isNegative && 'trend-down',
                isNeutral && 'trend-neutral',
              )}
            >
              {isPositive && <TrendingUp size={12} />}
              {isNegative && <TrendingDown size={12} />}
              {isNeutral && <Minus size={12} />}
              <span>
                {isPositive && '+'}
                {trend}% {trendLabel ?? 'vs. período anterior'}
              </span>
            </div>
          )}
        </div>

        <div
          className={cn(
            'w-12 h-12 rounded-[var(--radius-md)] flex items-center justify-center shrink-0 ml-4',
            colorClasses[color].icon,
          )}
        >
          {icon}
        </div>
      </div>
    </div>
  )
}
